import React, { useEffect, useState } from "react";
import { Empty, Spin, Table, type TablePaginationConfig, type TableProps } from "antd";
import styled from "styled-components";
import { ColumnsType } from "antd/es/table";
import { SortOrder, TableRowSelection } from "antd/es/table/interface";
import Text from "../Text/Text";
import icon from "../../resource/icon";
import color from "../../resource/color";

interface CustomTableProps {
  columns: ColumnsType<any>;
  data: any;
  size?: any;
  loading?: boolean;
  isPagination?: boolean;
  summary?: any;
  rowSelection?: TableRowSelection<any> | undefined;
  scrollX?: any;
  scrollY?: any;
  onScroll?: React.UIEventHandler<HTMLDivElement> | undefined;
  rowKey?: any;
  imgEmpty?: any;
  textEmpty?: string;
  colorHead?: boolean;
  footer?: any;
  expandable?: any;
  rowBackgroundColor?: string;
  isOpenExpand?: boolean;
  showHeader?: boolean;
  expandRowBackgroundColor?: any;
  left?: string;
  className?: string;
}

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: SortOrder;
  filters?: Record<string, any>;
}

const StyledTable = styled(Table)<
  TableProps<any> & {
    rowBackgroundColor?: string;
    expandRowBackgroundColor?: string;
    left?: string;
  }
>`
  .ant-table-cell {
    font-family: "IBM Plex Sans Thai", sans-serif;
    font-weight: 700 !important;
    font-size: 16px;
    color: ${color.Text1};
    background-color: ${(props) => props.expandRowBackgroundColor || "none"}!important;
  }

  .ant-table-thead > tr > th {
    background-color: ${color.background1};
    border-bottom: 0px;
    padding: 12px 16px;
  }

  .ant-table-expanded-row-fixed {
    font-weight: 400 !important;
  }

  .ant-table-row-expand-icon {
    width: 26px;
    height: 26px;
    transition: transform 0.3s ease;
    cursor: pointer;
    border: 0px;
    background: none;
  }
  .ant-table-expanded-row {
    background-color: ${(props) => props.expandRowBackgroundColor || "#f0f0f0"}!important;
  }
  .ant-table-expanded-row-fixed {
    padding: 0px !important;
    padding-left: ${(props) => props.left || "64px"}!important;
  }
`;

const ExpandTable: React.FC<CustomTableProps> = ({
  columns,
  data,
  summary,
  loading = false,
  rowSelection,
  scrollX = "auto",
  scrollY,
  onScroll,
  rowKey,
  imgEmpty,
  textEmpty = "ไม่พบข้อมูล",
  size,
  colorHead = false,
  footer,
  expandable,
  rowBackgroundColor,
  expandRowBackgroundColor,
  isOpenExpand = false,
  showHeader = true,
  left,
  className,
}) => {
  const [value, setValue] = useState<any>([]);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });
  const [expandedRowKeys, setExpandedRowKeys] = useState<any[]>([]);

  useEffect(() => {
    const fetchTableData = async () => {
      try {
        setValue(data);
        if (isOpenExpand) {
          const initialExpandedKeys = data.map((item: any) => {
            return rowKey && typeof rowKey === "function" ? rowKey(item, 0) : item[rowKey];
          });
          setExpandedRowKeys(initialExpandedKeys);
        } else {
          setExpandedRowKeys([]);
        }

        setTableParams((prevParams) => ({
          ...prevParams,
          pagination: {
            ...prevParams.pagination,
            total: data?.length,
          },
        }));
      } catch (error) {
        console.error("Error fetching table data:", error);
      }
    };

    fetchTableData();
  }, [data, isOpenExpand, rowKey]);

  const handleExpand = (expanded: boolean, record: any) => {
    const key = rowKey && typeof rowKey === "function" ? rowKey(record, 0) : record[rowKey];
    setExpandedRowKeys((prevKeys) => {
      if (expanded) {
        const newKeys = [...new Set([...prevKeys, key])];
        return newKeys;
      } else {
        const newKeys = prevKeys.filter((expandedKey) => expandedKey !== key);
        return newKeys;
      }
    });
  };

  return (
    <Spin size='large' spinning={loading}>
      <StyledTable
        className={className}
        showHeader={showHeader}
        rowBackgroundColor={rowBackgroundColor}
        rowSelection={rowSelection}
        columns={columns}
        dataSource={value || []}
        pagination={false}
        rowKey={rowKey}
        size={size}
        footer={footer}
        onScroll={onScroll}
        showSorterTooltip={false}
        expandable={
          expandable
            ? {
                ...expandable,
                expandIconColumnIndex: columns.length,
                expandIcon: ({ expanded, onExpand, record }) => (
                  <img
                    style={{ display: "flex" }}
                    src={expanded ? icon.expandUp : icon.expandDown}
                    onClick={(e) => onExpand(record, e)}
                    className='ant-table-row-expand-icon'
                  />
                ),
                expandedRowKeys,
                onExpand: handleExpand,
              }
            : undefined
        }
        scroll={{
          x: scrollX,
          y: scrollY,
        }}
        locale={{
          emptyText: (
            <div style={{ padding: "62px 0px 62px 0px" }}>
              <Empty image={imgEmpty || Empty.PRESENTED_IMAGE_SIMPLE} description={null} />
              <Text fontFamily='Sarabun' fontSize={14} fontWeight={400} color='Text3'>
                {textEmpty}
              </Text>
            </div>
          ),
        }}
        summary={summary}
        expandRowBackgroundColor={expandRowBackgroundColor}
        left={left}
      />
    </Spin>
  );
};

export default ExpandTable;
